import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../common/ConfirmDialog';

interface LocationGroupGrant {
  user_uuid: string;
  location_group_id: number;
  granted_by: string | null;
  granted_at: string;
  group_name: string | null;
  group_type: string | null;
  member_count: number;
}

interface LocationGroupOption {
  id: number;
  name: string;
  type: string;
  description: string | null;
  member_count: number;
}

interface UserLocationGroupsTabProps {
  userUuid: string;
}

export default function UserLocationGroupsTab({ userUuid }: UserLocationGroupsTabProps) {
  const [grants, setGrants] = useState<LocationGroupGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<LocationGroupGrant | null>(null);

  // Add dialog state
  const [groups, setGroups] = useState<LocationGroupOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<LocationGroupOption | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    loadGrants();
  }, [userUuid]);

  const loadGrants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<LocationGroupGrant[]>(
        `/admin/users/${userUuid}/location-groups`
      );
      setGrants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load location group grants');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedGroup(null);
    setAddError(null);

    try {
      setLoadingGroups(true);
      const allGroups = await apiClient.get<LocationGroupOption[]>('/admin/location-groups');
      const grantedIds = new Set(grants.map((g) => g.location_group_id));
      const available = allGroups.filter((g: LocationGroupOption) => !grantedIds.has(g.id));
      setGroups(available);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to load location groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAddGrant = async () => {
    if (!selectedGroup) return;

    try {
      setAddLoading(true);
      setAddError(null);
      await apiClient.post(`/admin/users/${userUuid}/location-groups`, {
        location_group_id: selectedGroup.id,
      });
      setAddDialogOpen(false);
      await loadGrants();
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to grant location group access'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveGrant = (grant: LocationGroupGrant) => {
    setSelectedGrant(grant);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedGrant) return;

    try {
      await apiClient.delete(
        `/admin/users/${userUuid}/location-groups/${selectedGrant.location_group_id}`
      );
      setRemoveDialogOpen(false);
      setSelectedGrant(null);
      await loadGrants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke location group access');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Location Group Access Grants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} size="small">
          Grant Group
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Locations</TableCell>
              <TableCell>Date Granted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grants.map((grant) => (
              <TableRow key={grant.location_group_id}>
                <TableCell>{grant.group_name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={grant.group_type || '-'}
                    color={grant.group_type === 'system' ? 'info' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{grant.member_count}</TableCell>
                <TableCell>{formatDate(grant.granted_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGrant(grant)}
                    title="Revoke access"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {grants.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No location group grants yet
          </Typography>
        </Box>
      )}

      {/* Grant Location Group Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Location Group Access</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            {loadingGroups ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Autocomplete
                options={groups}
                getOptionLabel={(option) => `${option.name} (${option.member_count} locations)`}
                value={selectedGroup}
                onChange={(_, newValue) => setSelectedGroup(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Location Group" placeholder="Search groups..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGrant}
            variant="contained"
            disabled={addLoading || !selectedGroup}
          >
            {addLoading ? 'Granting...' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={removeDialogOpen}
        title="Revoke Location Group Access"
        message={`Are you sure you want to revoke access to the "${selectedGrant?.group_name}" group from this user?`}
        confirmText="Revoke"
        confirmColor="error"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setRemoveDialogOpen(false);
          setSelectedGrant(null);
        }}
      />
    </Box>
  );
}
